import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Camera, X, Trash } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Compress an image File to a square data-URL, max ~300x300, JPEG quality 0.85
const compressToDataUrl = (file) => new Promise((resolve, reject) => {
  if (!file.type.startsWith('image/')) return reject(new Error('Please choose an image file'));
  if (file.size > 8 * 1024 * 1024) return reject(new Error('File too large (max 8 MB before compression)'));
  const reader = new FileReader();
  reader.onerror = () => reject(new Error('Could not read the file'));
  reader.onload = () => {
    const img = new Image();
    img.onload = () => {
      // Center-crop to square then scale down to max 300px
      const side = Math.min(img.naturalWidth, img.naturalHeight);
      const sx = (img.naturalWidth - side) / 2;
      const sy = (img.naturalHeight - side) / 2;
      const target = Math.min(300, side);
      const canvas = document.createElement('canvas');
      canvas.width = target;
      canvas.height = target;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, sx, sy, side, side, 0, 0, target, target);
      resolve(canvas.toDataURL('image/jpeg', 0.85));
    };
    img.onerror = () => reject(new Error('Could not decode the image'));
    img.src = reader.result;
  };
  reader.readAsDataURL(file);
});

const ProfilePictureModal = ({ open, onClose, currentPicture, onSaved }) => {
  const { user } = useAuth();
  const [preview, setPreview] = useState(currentPicture || '');
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  React.useEffect(() => {
    if (open) setPreview(currentPicture || '');
  }, [open, currentPicture]);

  if (!open) return null;

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const dataUrl = await compressToDataUrl(file);
      setPreview(dataUrl);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const save = async (clear = false) => {
    setSaving(true);
    try {
      const payload = { profile_picture: clear ? '' : preview };
      await axios.post(`${API_URL}/api/me/profile-picture`, payload, { withCredentials: true });
      toast.success(clear ? 'Profile picture removed' : 'Profile picture updated');
      onSaved?.(clear ? '' : preview);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Failed to save');
    }
    setSaving(false);
  };

  const initial = (user?.name || 'U').charAt(0).toUpperCase();

  return (
    <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4" onClick={onClose}>
      <Card className="w-full max-w-sm bg-white p-6" onClick={(e) => e.stopPropagation()} data-testid="profile-picture-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold" style={{ color: 'var(--dx-text)' }}>Profile picture</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="profile-modal-close"><X size={18} /></button>
        </div>
        <div className="flex flex-col items-center gap-4">
          <div
            className="w-32 h-32 rounded-full overflow-hidden flex items-center justify-center text-white text-4xl font-bold shadow-md border-4 border-white"
            style={{ background: 'var(--dx-primary, #1D9E75)' }}
          >
            {preview ? (
              <img src={preview} alt="" className="w-full h-full object-cover" data-testid="profile-modal-preview" />
            ) : (
              <span>{initial}</span>
            )}
          </div>
          <div className="text-xs text-gray-500 text-center">
            Square images work best.<br />Auto-cropped to 300×300 JPEG.
          </div>
          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            onChange={handleFile}
            style={{ display: 'none' }}
            data-testid="profile-picture-input"
          />
          <div className="flex flex-wrap justify-center gap-2 w-full">
            <Button onClick={() => fileInputRef.current?.click()} className="flex-1" data-testid="profile-pick-btn">
              <Camera size={16} className="mr-2" /> Choose photo
            </Button>
            {(currentPicture || preview) && (
              <Button
                variant="outline"
                onClick={() => save(true)}
                disabled={saving}
                className="text-rose-600 border-rose-200 hover:bg-rose-50"
                data-testid="profile-remove-btn"
              >
                <Trash size={16} className="mr-2" /> Remove
              </Button>
            )}
          </div>
          <div className="flex gap-2 w-full mt-2">
            <Button variant="outline" onClick={onClose} className="flex-1" data-testid="profile-cancel-btn">Cancel</Button>
            <Button
              onClick={() => save(false)}
              disabled={saving || !preview || preview === currentPicture}
              className="flex-1"
              data-testid="profile-save-btn"
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ProfilePictureModal;
